import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, CalendarDays, NotebookPen, Link2, Users,
  Sparkles, Settings, LogOut, Menu, X, GraduationCap, Crown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getPlanInfo } from "@/lib/plan";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, tid: "nav-calendar" },
  { to: "/planner", label: "Lesson Planner", icon: NotebookPen, tid: "nav-planner" },
  { to: "/resources", label: "Resource Hub", icon: Link2, tid: "nav-resources" },
  { to: "/students", label: "Students", icon: Users, tid: "nav-students" },
  { to: "/drafting", label: "Smart Drafting", icon: Sparkles, tid: "nav-drafting" },
  { to: "/settings", label: "Settings", icon: Settings, tid: "nav-settings" },
];

function RenewalBanner({ user, onRenew }) {
  const plan = getPlanInfo(user);
  if (!plan.expiring && !plan.expired) return null;
  return (
    <div
      data-testid="renewal-banner"
      className={`mb-6 rounded-2xl p-4 flex items-center justify-between gap-4 flex-wrap ${plan.expired ? "bg-destructive/10" : "bg-accent/30"}`}
    >
      <div className="flex items-center gap-3">
        <Crown size={20} className="text-primary shrink-0" />
        <p className="text-sm font-medium text-slate-700">
          {plan.expired
            ? "Your Pro plan has expired — renew to unlock calendar sync, AI drafting & more."
            : `Your Pro plan expires in ${plan.daysLeft} day${plan.daysLeft === 1 ? "" : "s"}. Renew to keep everything.`}
        </p>
      </div>
      <button onClick={onRenew} data-testid="renewal-banner-button" className="rounded-full bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 hover:bg-primary/90 transition-colors">
        Renew now
      </button>
    </div>
  );
}


export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = (user?.name || "T").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-6 py-7">
        <div className="h-11 w-11 rounded-2xl bg-primary flex items-center justify-center shadow-sm">
          <GraduationCap className="text-primary-foreground" strokeWidth={1.6} />
        </div>
        <div>
          <p className="font-heading font-extrabold text-lg leading-none text-slate-800">Command</p>
          <p className="text-xs text-slate-500 tracking-wide">teacher center</p>
        </div>
      </div>

      <nav className="flex-1 px-4 space-y-1.5">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.tid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-primary/15 text-primary font-semibold"
                  : "text-slate-600 hover:bg-slate-100"
              }`
            }
          >
            <item.icon size={19} strokeWidth={1.6} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 space-y-3">
        {!user?.is_pro && (
          <button
            onClick={() => { setOpen(false); navigate("/pricing"); }}
            data-testid="sidebar-upgrade-button"
            className="w-full text-left p-4 rounded-2xl bg-gradient-to-br from-primary/15 to-accent/25 hover:from-primary/25 transition-colors"
          >
            <div className="flex items-center gap-2 mb-1">
              <Crown size={16} className="text-primary" />
              <span className="font-heading font-bold text-slate-800 text-sm">Upgrade to Pro</span>
            </div>
            <p className="text-xs text-slate-500">Unlock calendar sync, AI drafting & more.</p>
          </button>
        )}
        <div className="tcc-card p-3 flex items-center gap-3">
          <Avatar className="h-10 w-10">
            {user?.picture ? <AvatarImage src={user.picture} alt={user?.name} /> : null}
            <AvatarFallback className="bg-secondary text-secondary-foreground font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1" data-testid="sidebar-username">
              {user?.name}
              {user?.is_pro && <Crown size={13} className="text-primary shrink-0" data-testid="pro-badge" />}
            </p>
            <p className="text-xs text-slate-400 truncate">{user?.is_pro ? "Pro plan" : user?.email}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={doLogout}
            className="p-2 rounded-xl text-slate-400 hover:text-destructive hover:bg-destructive/10 transition-colors"
            title="Log out"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background tcc-dots">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 bg-white/80 backdrop-blur-xl border-r border-white/60 z-30">
        <SidebarInner />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-5 py-4 bg-white/80 backdrop-blur-xl border-b border-white/60">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
            <GraduationCap className="text-primary-foreground" size={18} />
          </div>
          <span className="font-heading font-extrabold text-slate-800">Command</span>
        </div>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(true)} className="p-2 rounded-xl hover:bg-slate-100">
          <Menu />
        </button>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white shadow-2xl">
            <button className="absolute top-5 right-4 p-2" onClick={() => setOpen(false)}><X /></button>
            <SidebarInner />
          </div>
        </div>
      )}

      <main className="lg:pl-72">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="max-w-7xl mx-auto px-5 sm:px-8 py-8 sm:py-12"
        >
          <RenewalBanner user={user} onRenew={() => navigate("/pricing")} />
          {children}
        </motion.div>
      </main>
    </div>
  );
}
